// Cloud Functions API client

import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import {
  Project,
  ProjectInput,
  Persona,
  PersonaInput,
  Simulation,
  SimulationInput,
  Evaluation,
  EvaluationInput,
  ApiKeyInput,
} from '../types';

// Project functions
export const createProject = async (data: ProjectInput): Promise<Project> => {
  const fn = httpsCallable(functions, 'createProject');
  const result = await fn(data);
  return result.data as Project;
};

export const updateProject = async (projectId: string, data: Partial<ProjectInput>): Promise<void> => {
  const fn = httpsCallable(functions, 'updateProject');
  await fn({ projectId, ...data });
};

export const deleteProject = async (projectId: string): Promise<void> => {
  const fn = httpsCallable(functions, 'deleteProject');
  await fn({ projectId });
};

// Research functions
export const generateResearch = async (
  projectId: string,
  brandName: string,
  additionalInstructions?: string
): Promise<void> => {
  const fn = httpsCallable(functions, 'generateResearch', {
    timeout: 300000, // 5 minutes (research typically takes 1-3 minutes)
  });
  await fn({ projectId, brandOrCategory: brandName, additionalInstructions });
};

// Persona functions
export const generatePersonas = async (
  projectId: string,
  panelType: 'brand' | 'category' | 'b2b',
  count: number,
  medianHHI?: number
): Promise<Persona[]> => {
  const fn = httpsCallable(functions, 'generatePersonas', {
    timeout: 300000, // 5 minutes (persona generation typically takes 1-3 minutes)
  });
  const result = await fn({ projectId, panelType, count, medianHHI });
  return result.data as Persona[];
};

export const updatePersona = async (personaId: string, data: Partial<PersonaInput>): Promise<void> => {
  const fn = httpsCallable(functions, 'updatePersona');
  await fn({ personaId, ...data });
};

export const deletePersona = async (personaId: string): Promise<void> => {
  const fn = httpsCallable(functions, 'deletePersona');
  await fn({ personaId });
};

export const generatePersonaImage = async (personaId: string): Promise<string> => {
  const fn = httpsCallable(functions, 'generatePersonaImage', {
    timeout: 120000, // 2 minutes
  });
  const result = await fn({ personaId });
  return (result.data as any).avatarUrl;
};

// Simulation functions
export const runSimulation = async (data: SimulationInput): Promise<Simulation> => {
  const fn = httpsCallable(functions, 'runSimulation');
  const result = await fn(data);
  return result.data as Simulation;
};

export const deleteSimulation = async (simulationId: string): Promise<void> => {
  const fn = httpsCallable(functions, 'deleteSimulation');
  await fn({ simulationId });
};

// Evaluation functions
export const runEvaluation = async (data: EvaluationInput): Promise<Evaluation> => {
  const fn = httpsCallable(functions, 'runEvaluation');
  const result = await fn(data);
  return result.data as Evaluation;
};

export const deleteEvaluation = async (evaluationId: string): Promise<void> => {
  const fn = httpsCallable(functions, 'deleteEvaluation');
  await fn({ evaluationId });
};

// API Key functions
export const addApiKey = async (data: ApiKeyInput): Promise<void> => {
  const fn = httpsCallable(functions, 'addApiKey');
  await fn(data);
};

export const testApiKey = async (keyId: string): Promise<boolean> => {
  const fn = httpsCallable(functions, 'testApiKey');
  const result = await fn({ keyId });
  return result.data as boolean;
};

export const deleteApiKey = async (keyId: string): Promise<void> => {
  const fn = httpsCallable(functions, 'deleteApiKey');
  await fn({ keyId });
};

// File upload function
export const uploadFile = async (
  projectId: string,
  file: File,
  purpose: 'research_data' | 'creative_asset' | 'general'
): Promise<{ url: string; uploadId: string }> => {
  const fn = httpsCallable(functions, 'uploadFile');

  // Convert file to base64
  const reader = new FileReader();
  const fileData = await new Promise<string>((resolve, reject) => {
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const result = await fn({
    projectId,
    fileName: file.name,
    fileType: file.type,
    fileSize: file.size,
    fileData,
    purpose,
  });

  return result.data as { url: string; uploadId: string };
};

// Collaboration functions
export const inviteCollaborator = async (projectId: string, toEmail: string): Promise<void> => {
  const fn = httpsCallable(functions, 'inviteCollaborator');
  await fn({ projectId, toEmail });
};

export const respondToInvite = async (inviteId: string, response: 'accept' | 'decline'): Promise<any> => {
  const fn = httpsCallable(functions, 'respondToInvite');
  const result = await fn({ inviteId, response });
  return result.data;
};

export const removeCollaborator = async (projectId: string, userId: string): Promise<void> => {
  const fn = httpsCallable(functions, 'removeCollaborator');
  await fn({ projectId, userId });
};

export const leaveProject = async (projectId: string): Promise<void> => {
  const fn = httpsCallable(functions, 'leaveProject');
  await fn({ projectId });
};
